import { api } from '@/shared/api/api';
import { TourComponent } from '@/shared/components';
import CustomReactFlvPlayer from '@/shared/components/WrapperReactFlvPlayer/ReactFlvPlayer';
import imageSVG from '@/shared/images/dns-svgrepo-com.svg';
import { on } from '@/socket';
import { Col, ConfigProvider, Empty, notification, Row, Select, Switch, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTour } from '../../useTour';
import { RESOLUTION_OPTIONS } from './LiveStreams.constants';
import styles from './LiveStreams.module.css';
import { getStreamWidth } from './utils/getStreamWidth';

const { Option } = Select;
const { Title } = Typography;

const LiveStreams = () => {
  const [allStreams, setAllStreams] = useState([]);
  const [fetchWithAI, setFetchWithAI] = useState(false);
  const [apiNotification, contextHolder] = notification.useNotification();
  const [resolution, setResolution] = useState(RESOLUTION_OPTIONS.AUTO.value);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const {
    steps,
    isTourOpen,
    currentStep,
    startTour,
    handleTourChange,
    translationsListRef,
    translationsChooseRef,
    translationsGridRef,
    translationsAIRef,
    translationsFullScreenRef,
    handleClose,
  } = useTour(allStreams.length > 0);

  const abortControllerRef = useRef(null);

  const chosenStreams = useMemo(() => {
    return selectedOptions.length ? allStreams.filter(stream => selectedOptions.includes(stream.device_id)) : [];
  }, [allStreams, selectedOptions]);

  const displayedStreams = useMemo(() => {
    return chosenStreams.length ? chosenStreams : allStreams;
  }, [chosenStreams, allStreams]);

  //   const handleVisibilityChange = useCallback(() => {
  //     if (!document.hidden) {
  //       getAllStreams(fetchWithAI);
  //     } else {
  //       setAllStreams([]);
  //     }
  //   }, [fetchWithAI]);

  const getAllStreams = useCallback(async withAI => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const { data: response } = await api.get(withAI ? '/api/streams/vision_list?vision=True' : '/api/streams/list', {
        signal: abortController.signal,
      });
      setAllStreams(response);
      setSelectedOptions(prev =>
        response.filter(stream => prev.includes(stream.device_id)).map(stream => stream.device_id)
      );
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    getAllStreams(fetchWithAI);

    const eventName = fetchWithAI ? 'update_translate_vision' : 'update_translate';
    on(eventName, () => {
      getAllStreams(fetchWithAI);
    });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchWithAI]);

  const handleSelectChange = useCallback(value => {
    setSelectedOptions(value);
  }, []);

  const openNotification = useCallback(() => {
    apiNotification.info({
      message: 'AI+ (beta)',
      description:
        'Активен режим просмотра AI+ трансляции: В данном режиме возможна увеличенная задержка при просмотре трансляций!',
    });
  }, []);

  const getTitle = useCallback(el => el.call_sign || el.device_id, []);

  const handleSwitchChange = useCallback(checked => {
    if (checked) {
      openNotification();
    }
    setFetchWithAI(checked);
    setSelectedOptions([]);
  }, []);

  return (
    <>
      {contextHolder}
      <TourComponent
        handleClose={handleClose}
        handleOpen={startTour}
        isTourOpen={isTourOpen}
        steps={steps}
        onChange={handleTourChange}
        current={currentStep}
      />
      <Switch
        checkedChildren='AI+ (beta)'
        unCheckedChildren='Обычный'
        onChange={handleSwitchChange}
        className={styles.translationsSwitch}
        ref={translationsAIRef}
      />
      <div className={styles.selectBox}>
        <div ref={translationsChooseRef}>
          <ConfigProvider
            renderEmpty={() => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Нет доступных трансляций' />}
          >
            <Select
              mode='multiple'
              allowClear
              placeholder='Выберите трансляции'
              className={styles.translationsSelect__translations}
              value={selectedOptions}
              onChange={handleSelectChange}
            >
              {allStreams?.map(stream => (
                <Option key={stream.device_id} value={stream.device_id}>
                  {getTitle(stream)}
                </Option>
              ))}
            </Select>
          </ConfigProvider>
        </div>
        <div ref={translationsGridRef}>
          <Select
            placeholder={RESOLUTION_OPTIONS.AUTO.label}
            defaultValue={RESOLUTION_OPTIONS.AUTO.value}
            className={styles.translationsSelect__resolution}
            onChange={setResolution}
          >
            {Object.values(RESOLUTION_OPTIONS)?.map(resolution => (
              <Option key={resolution.value} value={resolution.value}>
                {resolution.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className={styles.streamsBox} ref={translationsListRef}>
        <Row style={{ width: '90%' }}>
          {displayedStreams.length === 0 ? (
            <div className={styles.emptyListPlaceholder}>
              <Empty image={imageSVG} description={<Title level={4}>Выберите трансляции для просмотра</Title>} />
            </div>
          ) : (
            displayedStreams.map(el => (
              <Col
                key={el.device_id}
                className={styles.streamItem}
                span={24}
                order={2}
                ref={translationsFullScreenRef}
                style={{
                  maxWidth: getStreamWidth(resolution, displayedStreams.length),
                }}
              >
                <div className={styles.userName}>{getTitle(el)}</div>
                {el.device_id && (
                  <CustomReactFlvPlayer
                    url={`/translation/live/${el.device_id}.flv/${el.creds}`}
                    hasAudio={false}
                    isLive={true}
                    enableStashBuffer={false}
                    stashInitialSize={0}
                    controls={false}
                    enableWarning={true}
                  />
                )}
              </Col>
            ))
          )}
        </Row>
      </div>
    </>
  );
};

export default LiveStreams;
